import Sortable from 'sortablejs'
import {Directive, nextTick} from 'vue'

/**
 * Movimento das janelas e dos cards do Kanban, num lugar só: as curvas, a regra de "reduzir
 * movimento" e as poucas animações que o Vuetify e o Sortable não fazem sozinhos.
 *
 * Tudo pela Web Animations API, e não por classe CSS: o que se anima aqui é um elemento que acabou
 * de mudar de lugar ou que já foi destruído pelo Vue, e não há componente montado para segurar uma
 * `<Transition>`.
 */

//As curvas do Vuetify (util/easing.js), para a saída da janela não destoar da entrada que ele faz
const STANDARD = 'cubic-bezier(0.4, 0, 0.2, 1)'
const DECELERATED = 'cubic-bezier(0.0, 0, 0.2, 1)'
const ACCELERATED = 'cubic-bezier(0.4, 0, 1, 1)'

/** Quanto um card leva para chegar ao lugar novo. */
const SLIDE_MS = 200

/** O sistema pediu menos movimento. O Vuetify consulta a mesma media query. */
export function reducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * A saída de uma janela que está sendo DESTRUÍDA, e não fechada.
 *
 * Os modais do app nascem com `v-if` no pai e `model-value` fixo em true: quem fecha é o pai,
 * tirando o `v-if`, e o `v-dialog` some no mesmo instante — sem a transição de saída, que o Vuetify
 * só faz quando o `model-value` vira false com ele ainda montado. A entrada ele já anima.
 *
 * Aqui a janela sai como CÓPIA: o `.v-overlay` inteiro é clonado ao lado do original, no mesmo
 * destino do Teleport, e o clone faz a saída que o Vuetify faria — o fundo apagando e o conteúdo
 * encolhendo para 0.9, com os tempos e a curva dele. Terminada, a cópia sai.
 *
 * Com `target` — o card de onde a janela nasceu, ver o `markOrigin` —, ela encolhe de volta para
 * ele, como o Vuetify faz na saída de uma janela com alvo. Sem alvo, ou com ele fora da tela, encolhe
 * no lugar. Expandida, cresce até a página. Quem escolhe entre essas saídas é o `exitOf`.
 *
 * A cópia é inerte (ver `.ontos-leaving` no Dialog.vue): o clique seguinte já é da tela de baixo.
 */
export function leaveOverlay(overlay: HTMLElement, target?: Element | null) {
    const ghost = overlay.cloneNode(true) as HTMLElement
    ghost.classList.add('ontos-leaving')
    ghost.inert = true
    overlay.after(ghost)
    const content = ghost.querySelector<HTMLElement>(':scope > .v-overlay__content')
    const scrim = ghost.querySelector<HTMLElement>(':scope > .v-overlay__scrim')
    const exit = content ? exitOf(content, target) : null
    const animations = [
        exit && content?.animate(exit.keyframes, {duration: exit.duration, easing: exit.easing, fill: 'forwards'}),
        //Mudando de forma — até o card, até a página —, o conteúdo some logo no começo, senão se vê o
        //texto espremido ou esticado
        ...(exit?.reshapes ? childrenOf(content).map(child => child.animate(
            [{}, {opacity: 0, offset: 0.2}, {opacity: 0}],
            {duration: exit.duration * 2, easing: STANDARD, fill: 'forwards'})) : []),
        scrim?.animate([{}, {opacity: 0}], {duration: 225, easing: STANDARD, fill: 'forwards'})
    ].filter((animation): animation is Animation => !!animation)
    Promise.all(animations.map(animation => animation.finished)).finally(() => ghost.remove())
}

/**
 * Como a janela sai, das quatro maneiras:
 *
 * - **para a página**, quando ela foi EXPANDIDA (ver o `markExpanding`): cresce até a área de
 *   conteúdo e se dissolve nela, enquanto a ficha da rota entra por baixo. É o contrário de fechar,
 *   e por isso o movimento é o contrário: encolher ali — de volta ao card, ainda por cima — dizia
 *   "voltou para onde estava" justamente quando a ação foi "vira a página".
 * - **para o card** de onde nasceu (`target`), encolhendo até ele;
 * - **no lugar**, encolhendo para 0.9, que é a saída do Vuetify;
 * - **só apagando**, com "reduzir movimento".
 */
function exitOf(content: HTMLElement, target?: Element | null) {
    if (reducedMotion()) return {keyframes: [{}, {opacity: 0}], duration: 85, easing: ACCELERATED, reshapes: false}
    if (expanding()) {
        const page = reshape(content, pageArea())
        //Acesa na primeira metade: o crescimento é o que se tem de ver
        if (page) return {
            keyframes: [{}, {opacity: 1, offset: 0.5}, {opacity: 0, transform: page.transform}],
            duration: EXPAND_MS, easing: STANDARD, reshapes: true
        }
    }
    const card = towards(target, content)
    if (card) return {
        keyframes: [{}, {opacity: 0, transform: card.transform}],
        duration: 125 * card.speed, easing: ACCELERATED, reshapes: true
    }
    return {keyframes: [{}, {opacity: 0, transform: 'scale(0.9)'}], duration: 125, easing: ACCELERATED, reshapes: false}
}

/** Quanto a janela leva para virar a página. Mais que fechar: é um movimento para ser visto. */
const EXPAND_MS = 300

/**
 * A área de conteúdo da página — o `v-main`, abaixo das barras —, recortada ao que se vê. É onde a
 * ficha da rota vai aparecer.
 */
function pageArea(): DOMRect {
    const main = document.querySelector('.v-main')?.getBoundingClientRect()
    const top = Math.max(main?.top || 0, 0)
    const bottom = Math.min(main?.bottom || window.innerHeight, window.innerHeight)
    const left = main?.left || 0
    return new DOMRect(left, top, (main?.width || window.innerWidth), bottom - top)
}

/**
 * O transform que leva o conteúdo a ocupar exatamente `box`, esticando nas duas direções. Diferente
 * do `towards`, que preserva a proporção, como o Vuetify faz ao nascer do card.
 */
function reshape(content: HTMLElement, box: DOMRect) {
    const el = content.getBoundingClientRect()
    if (!el.width || !el.height || box.height <= 0) return null
    const x = box.left + box.width / 2 - (el.left + el.width / 2)
    const y = box.top + box.height / 2 - (el.top + el.height / 2)
    return {transform: `translate(${x}px, ${y}px) scale(${box.width / el.width}, ${box.height / el.height})`}
}

/**
 * A janela que sair agora vai para a página: o Expandir anota antes de navegar, como o clique anota
 * a origem (ver o `markOrigin`). Quem expande é o `DialogActions`, o canto de toda janela.
 *
 * Vale por um instante e não se consome: expandindo uma ficha aberta POR CIMA de outra, a navegação
 * leva as duas janelas, e as duas viram a página — a de baixo encolhendo para o card dela, enquanto a
 * de cima cresce, desmentiria o gesto.
 */
let expandedAt = -Infinity

export function markExpanding() {
    expandedAt = performance.now()
}

function expanding(): boolean {
    return performance.now() - expandedAt < 1000
}

/**
 * A janela muda de altura deslizando, e não de um quadro para o outro.
 *
 * O conteúdo de uma janela cresce sozinho: a ficha abre com o que o card já sabia e relê por trás,
 * e quando a leitura chega as seções entram todas de uma vez. Centralizada, a janela pulava para
 * cima e para baixo ao mesmo tempo. CSS não resolve: não há propriedade mudando para uma `transition`
 * pegar — a altura é `auto` antes e depois, o que muda é o que tem dentro.
 *
 * Então se observa o tamanho (`ResizeObserver`) e, a cada mudança, anima da altura antiga para a
 * nova. O callback roda depois do layout e antes da pintura, e por isso o tamanho novo não chega a
 * aparecer antes de a animação começar.
 *
 * Enquanto anima, as mudanças que o observador vê são a própria animação, e são ignoradas. Se o
 * conteúdo mudou de novo nesse meio-tempo, o fim da animação devolve a altura natural, o observador
 * acusa a diferença, e uma segunda animação leva até ela.
 *
 * Algumas mudanças só se ACOMPANHAM, sem animar:
 * - **as que não são de conteúdo chegando**: o navegador sendo redimensionado, o elemento saindo de
 *   um `display: none` (ver o `willAnimate`, que é quem diz);
 * - **a de quem tem dentro algo já crescendo devagar**: a janela da ficha cujas seções estão
 *   deslizando, a seção cujo texto se expande por `transition`. A mudança já é suave, e uma segunda
 *   animação por cima andaria aos trancos atrás dela.
 *
 * Devolve a função que para de observar.
 */
export function smoothHeight(el: HTMLElement): () => void {
    const sized: Sized = {el, height: el.offsetHeight, width: el.offsetWidth}
    let resizing: Animation | null = null
    const observer = new ResizeObserver(() => {
        if (resizing) return
        const animates = willAnimate(sized)
        const from = sized.height
        sized.height = el.offsetHeight
        sized.width = el.offsetWidth
        if (!animates || reducedMotion() || growingInside(el)) return
        //`overflow: hidden` só durante: encolhendo, o conteúdo ainda alto vazaria da janela
        resizing = el.animate([
            {height: `${from}px`, overflow: 'hidden'},
            {height: `${sized.height}px`, overflow: 'hidden'}
        ], {duration: RESIZE_MS, easing: STANDARD, id: RESIZING})
        resizing.finished.then(() => resizing = null, () => resizing = null)
        holdScrolling(el)
    })
    observer.observe(el)
    smoothed.add(sized)
    return () => {
        observer.disconnect()
        smoothed.delete(sized)
    }
}

/** Um elemento com a altura deslizando, e o último tamanho que ele viu em si. */
type Sized = {el: HTMLElement, height: number, width: number}

/**
 * A mudança desde o último tamanho visto é das que animam: a altura mudou, e não por um destes dois:
 *
 * - **a janela do navegador sendo redimensionada**: animar faria a página inteira se arrastar atrás
 *   do redimensionamento;
 * - **o elemento saindo de um `display: none`**, que vai de zero a tudo (a ficha monta as seções
 *   escondidas enquanto carrega). Nada estava na tela para deslizar.
 *
 * NÃO é "a largura mudou", como chegou a ser: a barra de rolagem da página aparecendo também muda a
 * largura de tudo, e ela aparece justamente quando o conteúdo cresce além da tela. Na rota da ficha,
 * voltando da edição, a prévia curta virava a ficha completa, a barra surgia e nenhuma seção animava
 * (relato do usuário). A barra não dispara o `resize` da janela, e é por ele que se distingue.
 */
function willAnimate({el, height, width}: Sized): boolean {
    return Math.abs(el.offsetHeight - height) >= 1 && width > 0 && el.offsetWidth > 0
        && performance.now() - windowResizedAt > 300
}

/** Quando a janela do navegador mudou de tamanho pela última vez — ver o `willAnimate`. */
let windowResizedAt = -Infinity
if (typeof window !== 'undefined') window.addEventListener('resize', () => windowResizedAt = performance.now())

/** Todos os que deslizam agora — para um contêiner saber quem, dentro dele, vai animar. */
const smoothed = new Set<Sized>()

/**
 * O que rola logo dentro do elemento não rola enquanto a altura dele anima.
 *
 * Crescendo, a altura parte da antiga, menor que o conteúdo: o card da janela, que tem
 * `overflow-y: auto`, mostrava a barra de rolagem por um instante, a barra roubava largura e o
 * conteúdo inteiro tremia para dentro (relato do usuário, ao trocar Lista e Kanban na ficha).
 */
function holdScrolling(el: HTMLElement) {
    for (const child of el.children) {
        if (!['auto', 'scroll'].includes(getComputedStyle(child).overflowY)) continue
        child.animate([{overflowY: 'hidden'}, {overflowY: 'hidden'}], {duration: RESIZE_MS})
    }
}

/** Quanto a altura leva para chegar ao tamanho novo. Um pouco mais que o card: é mais pano. */
const RESIZE_MS = 250

/** A marca das animações do `smoothHeight`, para um contêiner reconhecê-las por dentro. */
const RESIZING = 'ontos-resizing'

/**
 * Algo dentro do elemento está mudando de altura aos poucos — ver o `smoothHeight`.
 *
 * Também o que VAI animar: uma seção de dentro que mudou de tamanho neste mesmo quadro. Os
 * observadores são chamados em ordem de criação, o de fora primeiro, e quando ele pergunta a animação
 * da seção ainda não existe — sem esta checagem, os dois animavam por cima um do outro.
 *
 * A pergunta é a MESMA que a seção vai se fazer (`willAnimate`), e não "mudou de altura". Com a
 * segunda, as seções da ficha saindo do `display: none` — que mudam, mas não animam — faziam a
 * janela só acompanhar, e ninguém animava: a janela crescia de uma vez quando a ficha terminava de
 * carregar (relato do usuário, abrindo uma pessoa pelo link da célula).
 */
function growingInside(el: HTMLElement): boolean {
    for (const inner of smoothed) {
        if (inner.el !== el && el.contains(inner.el) && willAnimate(inner)) return true
    }
    return el.getAnimations({subtree: true}).some(animation => animation.playState === 'running'
        && (animation.id === RESIZING
            || (animation instanceof CSSTransition && animation.transitionProperty.includes('height'))))
}

/**
 * O conteúdo de uma tela acende quando chega, e não aparece num quadro só.
 *
 * É o fim do padrão de carga de toda tela (ver o `ontos-base`): a barra corre, a leitura chega e a
 * tela, que estava vazia, se enchia de uma vez. Aqui ela entra apagada e 8px abaixo, e assenta
 * acendendo — a entrada do Material, a mesma das listas e menus do Vuetify.
 *
 * Já foi uma cortina descendo (um `clip-path` descobrindo de cima para baixo), e o usuário achou
 * artificial: nada se descobre por um recorte. O fade com um passo curto lê como coisa que chega.
 *
 * Quem decide QUANDO é o observador: o elemento estava vazio (sem altura de conteúdo) e deixou de
 * estar. Não depende de a tela avisar que carregou — ela pode ter conteúdo antes de a barra parar (o
 * `afterLoad`) ou de cara (a ficha que chega com a prévia). Devolve a função que para de observar.
 */
export function revealOnArrival(el: HTMLElement): () => void {
    let empty = true
    const observer = new ResizeObserver(([entry]) => {
        const wasEmpty = empty
        empty = entry.contentRect.height < 8
        if (wasEmpty && !empty) reveal(el)
    })
    observer.observe(el)
    return () => observer.disconnect()
}

function reveal(el: HTMLElement) {
    if (reducedMotion()) return
    el.animate([{opacity: 0, transform: 'translateY(8px)'}, {}], {duration: REVEAL_MS, easing: DECELERATED})
}

/** Quanto o conteúdo leva para acender. */
const REVEAL_MS = 250

/**
 * O `smoothHeight` como diretiva, para quem não segura o elemento: `v-smooth-height` numa tag (ou na
 * raiz de um componente) e a altura dela passa a deslizar enquanto ela existir.
 */
const stopsSmoothing = new WeakMap<HTMLElement, () => void>()

export const vSmoothHeight: Directive<HTMLElement> = {
    mounted(el) {
        stopsSmoothing.set(el, smoothHeight(el))
    },
    beforeUnmount(el) {
        stopsSmoothing.get(el)?.()
        stopsSmoothing.delete(el)
    }
}

/**
 * O transform que leva o conteúdo da janela ao card, e a velocidade — a mesma conta do
 * `getDimensions` do VDialogTransition, para a volta casar com a ida que ele fez.
 *
 * Null quando não há para onde voltar: o card saiu da tela ou do documento (a raia foi
 * reagrupada, a lista foi relida).
 */
function towards(target: Element | null | undefined, content: HTMLElement) {
    if (!target?.isConnected || !target.getClientRects().length) return null
    const box = target.getBoundingClientRect()
    const el = content.getBoundingClientRect()
    if (!el.width || !el.height) return null
    const sx = box.width / el.width
    const sy = box.height / el.height
    const max = Math.max(1, sx, sy)
    //Janela grande demora até 1,5x mais para ir, e por isso para voltar
    const area = el.width * el.height / (window.innerWidth * window.innerHeight)
    const speed = area > 0.12 ? Math.min(1.5, (area - 0.12) * 10 + 1) : 1
    const x = box.left + box.width / 2 - (el.left + el.width / 2)
    const y = box.top + box.height / 2 - (el.top + el.height / 2)
    return {transform: `translate(${x}px, ${y}px) scale(${sx / max}, ${sy / max})`, speed}
}

/** O que o Vuetify apaga por dentro da janela: os filhos do card dela. */
function childrenOf(content: HTMLElement | null): HTMLElement[] {
    const card = content?.querySelector(':scope > .v-card, :scope > .v-sheet, :scope > .v-list')
    return card ? [...card.children] as HTMLElement[] : []
}

/**
 * O elemento de onde a próxima janela nasce.
 *
 * Quem sabe o que foi clicado é o clique, e quem abre a janela é outro componente, às vezes dois
 * eventos adiante (o card emite, o quadro emite, a página abre). Passar o elemento por cada um
 * desses caminhos seria um parâmetro em todos; aqui o clique anota, e a janela, ao nascer, leva.
 *
 * A anotação vale pouco: um segundo. Clique que não abriu janela nenhuma — o que navegou, por
 * exemplo — não pode dar origem a uma janela aberta depois por outro caminho.
 */
let origin: {el: Element, at: number} | null = null

export function markOrigin(el: EventTarget | null) {
    origin = el instanceof Element ? {el, at: Date.now()} : null
}

/** A origem anotada, uma vez — ou null, se ela já venceu ou saiu da tela. */
export function takeOrigin(): Element | null {
    const taken = origin
    origin = null
    return taken && taken.el.isConnected && Date.now() - taken.at < 1000 ? taken.el : null
}

/**
 * Desliza um elemento de onde ele ESTAVA (`from`, em coordenadas de janela) até onde está agora.
 *
 * O elemento já está no lugar novo; o que viaja é uma cópia, por cima de tudo, enquanto ele espera
 * invisível. Transladar o próprio elemento não serve entre raias: a raia é um `v-card`, que tem
 * `overflow: hidden`, e o card viajando de outra raia ficaria recortado até entrar na dele.
 *
 * A cópia vai para dentro do `.v-application`, e não do `body`: as cores do Vuetify são variáveis
 * declaradas ali, e fora dele o card perderia o fundo e o texto.
 */
export function flyFrom(el: HTMLElement, from: DOMRect | null) {
    if (!from || reducedMotion()) return
    land(el)
    const to = el.getBoundingClientRect()
    if (Math.abs(from.left - to.left) < 1 && Math.abs(from.top - to.top) < 1) return
    const ghost = floatingCopy(el, to)
    el.style.visibility = 'hidden'
    const flight = ghost.animate([
        {transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)`},
        {transform: 'none'}
    ], {duration: SLIDE_MS, easing: STANDARD})
    const done = () => {
        ghost.remove()
        el.style.visibility = ''
        flights.delete(el)
    }
    flights.set(el, done)
    flight.finished.then(done, done)
}

/** As viagens em curso, para uma nova poder encerrar a anterior do mesmo elemento. */
const flights = new WeakMap<HTMLElement, () => void>()

/**
 * Uma cópia inerte do elemento, por cima da tela em que ele está, em `at` (coordenadas de janela). É o
 * que viaja no `flyFrom` e o que se despede no `leave`.
 *
 * Dentro do `.v-application`, e não do `body`: as cores do Vuetify são variáveis declaradas ali, e
 * fora dele o card perderia o fundo e o texto.
 *
 * **Na camada do próprio card**, e não por cima de tudo: era `z-index: 3000`, acima de toda janela, e o
 * card que voltava à raia depois de um arrasto recusado passava por cima da janela de recusa (relato do
 * usuário). Agora, dentro de uma janela — o quadro da ficha —, a cópia vai para a camada dela
 * (`.v-overlay`, que é quem empilha as janelas) e não sai de lá; na página, fica abaixo de toda janela
 * (as do Vuetify começam em 2000) e acima das raias. Não o `.v-overlay__content`: ele tem contenção de
 * layout, e o `position: fixed` passaria a contar dele, e não da janela.
 */
function floatingCopy(el: HTMLElement, at: DOMRect): HTMLElement {
    const ghost = el.cloneNode(true) as HTMLElement
    ghost.inert = true
    Object.assign(ghost.style, {
        position: 'fixed', left: `${at.left}px`, top: `${at.top}px`,
        width: `${at.width}px`, height: `${at.height}px`, margin: '0', zIndex: '1000',
        pointerEvents: 'none'
    })
    ;(el.closest('.v-overlay') || document.querySelector('.v-application') || document.body).appendChild(ghost)
    return ghost
}

/**
 * O card que deixou o quadro sai apagando e encolhendo um pouco — o contrário da entrada do card
 * novo. Quem sai é uma cópia, no lugar onde ele estava: o Vue já o tirou da tela, e sem isto ele
 * sumia num quadro, com os vizinhos deslizando para um buraco que ninguém viu abrir (pedido do
 * usuário: criados, removidos e desvinculados com transição, em todo Kanban).
 */
function leave(el: HTMLElement, at: DOMRect) {
    const ghost = floatingCopy(el, at)
    ghost.animate([{}, {opacity: 0, transform: 'scale(0.95)'}], {duration: SLIDE_MS, easing: ACCELERATED})
        .finished.finally(() => ghost.remove())
}

/** Encerra o que estiver animando o elemento, deixando-o parado e visível no lugar dele. */
function land(el: HTMLElement) {
    flights.get(el)?.()
    el.getAnimations().forEach(animation => animation.cancel())
}

/**
 * Onde cada card está, para o `animateFrom` comparar depois.
 *
 * A chave é o `data-card` mais a ocorrência: numa dimensão múltipla o mesmo item aparece em mais
 * de uma raia, e a segunda cópia não pode sair do lugar da primeira.
 */
export type CardPlaces = Map<string, {rect: DOMRect, list: Element | null, el: HTMLElement}>

export function cardPlaces(root: Element | null | undefined): CardPlaces {
    const places: CardPlaces = new Map()
    if (!root?.querySelectorAll) return places
    eachCard(root, (el, key) => places.set(key, {rect: el.getBoundingClientRect(), list: el.parentElement, el}))
    return places
}

/**
 * Leva cada card do lugar antigo ao novo: é o FLIP de um reagrupamento.
 *
 * - **Mesma lista**: translada o próprio card. São os vizinhos acomodando um que entrou ou saiu, e
 *   a raia não recorta ninguém.
 * - **Outra lista**: o `flyFrom`, por cima — é o card voltando para a raia de onde saiu.
 * - **Card novo**: aparece crescendo um pouco, sem viajar de lugar nenhum.
 * - **Card que saiu** — excluído, desvinculado, escondido pelo prazo: sai apagando (`leave`).
 *
 * Sem foto anterior, nada: é a primeira montagem, e o quadro inteiro surgindo em movimento seria
 * ruído.
 */
export function animateFrom(root: Element | null | undefined, before: CardPlaces) {
    if (!before.size || !root?.querySelectorAll || reducedMotion()) return
    const present = new Set<string>()
    eachCard(root, (el, key) => {
        present.add(key)
        const was = before.get(key)
        if (!was) {
            el.animate([{opacity: 0, transform: 'scale(0.95)'}, {}], {duration: SLIDE_MS, easing: DECELERATED})
            return
        }
        if (was.list !== el.parentElement) return flyFrom(el, was.rect)
        land(el)
        const to = el.getBoundingClientRect()
        const dx = was.rect.left - to.left
        const dy = was.rect.top - to.top
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return
        el.animate([{transform: `translate(${dx}px, ${dy}px)`}, {transform: 'none'}],
            {duration: SLIDE_MS, easing: STANDARD})
    })
    before.forEach((was, key) => {
        if (!present.has(key) && !root.contains(was.el)) leave(was.el, was.rect)
    })
}

function eachCard(root: Element, visit: (el: HTMLElement, key: string) => void) {
    const seen: Record<string, number> = {}
    root.querySelectorAll<HTMLElement>('[data-card]').forEach(el => {
        const id = el.dataset.card as string
        seen[id] = (seen[id] || 0) + 1
        visit(el, `${id}#${seen[id]}`)
    })
}

/**
 * Chama a atenção para um elemento: traz para a vista e acende no azul de destaque do Kanban, que
 * apaga devagar.
 */
export function pulse(el: HTMLElement | null | undefined) {
    if (!el) return
    el.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth'})
    if (reducedMotion()) return
    el.animate([{backgroundColor: 'var(--ontos-kanban-highlight)'}, {}], {duration: 1200, easing: STANDARD})
}

/**
 * Onde estava o clone do arrasto no instante em que o card foi solto.
 *
 * É de onde o card parte ao assentar. Só o Sortable sabe, e por pouco tempo: no `end` que o
 * vuedraggable repassa, o clone já foi removido. O gancho `drop` de um plugin roda ANTES disso, e é
 * o ponto de extensão que ele oferece para o caso.
 *
 * Vale só nas listas que pedem: a opção `dropRect` no `draggable` (é como o Sortable liga plugin).
 */
let dropRect: DOMRect | null = null

class DropRect {
    static pluginName = 'dropRect'

    drop() {
        dropRect = Sortable.ghost?.getBoundingClientRect() || null
    }
}

//Uma vez por carga: o Sortable recusa o mesmo nome duas vezes, e o recarregamento a quente do
//servidor de desenvolvimento reavalia este módulo. Depois de um recarregamento DESTE arquivo o
//plugin que vale é o antigo, que escreve na variável antiga — o card para de assentar até um F5.
try {
    Sortable.mount(DropRect)
} catch {
    //já montado
}

/** O retângulo do último drop, uma vez: lido, ele é esquecido. */
function takeDropRect(): DOMRect | null {
    const rect = dropRect
    dropRect = null
    return rect
}

/**
 * As opções de uma lista arrastável cujo item ASSENTA ao ser solto — o par do `settleDrop`, que vai no
 * `@end`. Num `v-bind` do `draggable`:
 * - `animation`: os vizinhos deslizam para abrir o lugar, em vez de pularem;
 * - `forceFallback`: o item viaja como um clone que o Sortable desenha, e não pelo arrasto nativo do
 *   navegador — é a posição DESSE clone que o `dropRect` guarda ao soltar;
 * - `fallbackOnBody`: o clone vai para o body, e não fica recortado pela lista ou pela raia;
 * - `dropRect`: liga o plugin acima.
 *
 * Um lugar só porque são duas listas a pedir o mesmo: as raias do Kanban e as propriedades da coleção.
 */
export const settlingDrag = {animation: 150, forceFallback: true, fallbackOnBody: true, dropRect: true}

/**
 * O item assenta: sai de onde o clone do arrasto estava e desliza até o lugar em que caiu — ou de volta
 * ao de origem, quando foi solto fora da lista. Para o `@end` de um `draggable` com o `settlingDrag`.
 *
 * No `nextTick` porque o vuedraggable não move o elemento arrastado: ele o tira e deixa o Vue desenhar o
 * item na lista de destino, que entre raias é OUTRO elemento. O `to` e o `newIndex` do evento dizem onde
 * procurá-lo.
 */
export function settleDrop(event: {to: HTMLElement, newIndex: number}) {
    const from = takeDropRect()
    nextTick(() => flyFrom(event.to.children[event.newIndex] as HTMLElement, from))
}
